import { useEffect, useRef, useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";

const STATUSES = [
    { value: "online", label: "Online", color: "bg-emerald-500", text: "text-emerald-600" },
    { value: "break", label: "On Break", color: "bg-amber-500", text: "text-amber-600" },
    { value: "offline", label: "Offline", color: "bg-stone-400", text: "text-stone2" },
];

/** Agent availability control with a background heartbeat so routing knows who's reachable. */
export default function PresenceControl({ compact = false, autoReady = false }) {
    const [status, setStatus] = useState("offline");
    const [open, setOpen] = useState(false);
    const hbRef = useRef(null);
    const autoOnRef = useRef(false);

    const stopHeartbeat = () => {
        if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
    };
    const startHeartbeat = () => {
        stopHeartbeat();
        hbRef.current = setInterval(() => { api.post("/me/heartbeat").catch(() => {}); }, 30000);
    };

    useEffect(() => {
        api.get("/me/presence")
            .then(({ data }) => {
                if (autoOnRef.current) return; // auto-presence took over; don't override
                setStatus(data.status);
                if (data.status === "online" || data.status === "break") startHeartbeat();
            })
            .catch(() => {});
        return stopHeartbeat;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const change = async (val) => {
        setStatus(val);
        setOpen(false);
        try { await api.put("/me/presence", { status: val }); } catch (_e) { /* ignore */ }
        if (val === "online" || val === "break") startHeartbeat();
        else stopHeartbeat();
    };

    // Auto-presence: when the host (e.g. Salesforce softphone) connects, mark the
    // agent Online; revert to Offline when the widget unmounts/closes.
    useEffect(() => {
        if (!autoReady || autoOnRef.current) return;
        autoOnRef.current = true;
        change("online");
        return () => {
            stopHeartbeat();
            api.put("/me/presence", { status: "offline" }).catch(() => {});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoReady]);

    const current = STATUSES.find((s) => s.value === status) || STATUSES[2];

    if (compact) {
        return (
            <div className="relative" data-testid="presence-control-compact">
                <button
                    data-testid="presence-toggle"
                    onClick={() => setOpen((o) => !o)}
                    className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] transition-colors hover:border-ink"
                >
                    <span className={`h-2 w-2 shrink-0 rounded-full ${current.color} ${status === "online" ? "animate-pulse" : ""}`} />
                    <span className={`whitespace-nowrap font-semibold ${current.text}`}>{current.label}</span>
                    <CaretDown size={11} className={`shrink-0 text-stone2 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                    <div className="absolute right-0 top-full z-40 mt-1 w-36 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl">
                        {STATUSES.map((s) => (
                            <button
                                key={s.value}
                                data-testid={`presence-${s.value}`}
                                onClick={() => change(s.value)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-stone-50"
                            >
                                <span className={`h-2 w-2 rounded-full ${s.color}`} />
                                <span className="font-medium">{s.label}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative mb-3" data-testid="presence-control">
            <button
                data-testid="presence-toggle"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm transition-colors hover:border-ink"
            >
                <span className={`h-2.5 w-2.5 rounded-full ${current.color} ${status === "online" ? "animate-pulse" : ""}`} />
                <span className={`flex-1 text-left font-semibold ${current.text}`}>{current.label}</span>
                <CaretDown size={13} className={`text-stone2 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute bottom-full left-0 z-30 mb-1 w-full overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl">
                    {STATUSES.map((s) => (
                        <button
                            key={s.value}
                            data-testid={`presence-${s.value}`}
                            onClick={() => change(s.value)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-stone-50"
                        >
                            <span className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                            <span className="font-medium">{s.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
